import { TileService } from '@/core/services/open-api/tile.service';
import { LayerService } from '@/core/services/open-api/layer.service';
import { UserService } from '@/core/services/open-api/user.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { DateRangeHelper } from '@/core/services/utils/date-range-helper';
import { Layer } from '@/core/models/layer';
import { Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CurrentUser } from '@/core/models/user';
import { Tile } from '@/core/models/tile';
import { SimpleAssignmentOptionItem, SimpleAssignmentOptions } from '@/core/models/assignment-options.interface';
import { GroupService } from '@/core/services/open-api/group.service';
import { TileType } from '@/core/models/tile-type';
import { Account, UserForm } from '@/core/models/account';
import { GroupForm } from '@/core/models/group-form';
import { SelectedItem } from '@/core/models/selected-item.interface';
import { AngularTranslations, MultiSelectOnChangeEvent } from '@/core/types/angular-primeng-api';
import { BreadcrumbService } from '@/theme/service/breadcrumb.service';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';
import { SharedFunctions } from '@/core/utils/shared-functions';
import { DateRangeChangedEvent } from '@/common/shared/date-range-selector/date-range-selector.component';
import { CloneUtils } from '@/core/utils/clone-utils';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { LoadingService } from '@/core/services/loading.service';
import { AnalyzeFilters, LayerFilter, UserFilter, TeamFilterOption, analyzeFiltersType } from './analyze-filters';
import '@/common/shared/arktype.config';
import { type } from 'arktype';
import { TrackingService } from '@/core/services/tracking.service';

export enum AnalyzeState {
    overview = 'overview',
    detail = 'detail',
}

@Component({
    selector: 'app-analyze',
    templateUrl: './analyze.component.html',
    styleUrls: ['./analyze.component.scss'],
    standalone: false,
})
export class AnalyzeComponent implements OnInit, OnDestroy {
    analyzeState: AnalyzeState = AnalyzeState.overview;
    stateOptions: SelectedItem[] = [];
    licenseStatePro = false;
    userDateFormat = '';
    hasData = false;
    firstDayOfWeek = 0;
    filterDescriptionDisabled = false;
    layers: Layer[] = [];
    // filter
    usersFilterOptions: UserFilter[] = [];
    activeUsersFilterOptions: UserFilter[] = [];
    layer1FilterOptions: LayerFilter[] = [];
    layer2FilterOptions: LayerFilter[] = [];
    layer3FilterOptions: LayerFilter[] = [];
    eventsSubject: Subject<AnalyzeFilters> = new Subject<AnalyzeFilters>();
    subscriptions: Subscription = new Subscription();
    teamOptions: SimpleAssignmentOptions<TeamFilterOption>[] = [];
    currentUser: CurrentUser | undefined;
    tiles: Tile[] = [];
    // selected filters (stored in AnalyzeFilters object):
    filters!: AnalyzeFilters;
    appliedFilters!: AnalyzeFilters;
    isAdminOrManager: boolean = false;
    initialized = false;
    initMgr: InitializationManager;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        public readonly userService: UserService,
        private readonly layerService: LayerService,
        private readonly tileService: TileService,
        private readonly translate: TranslateService,
        private readonly trackingService: TrackingService,
        private readonly groupService: GroupService,
        private readonly breadcrumbService: BreadcrumbService,
        private readonly loadingService: LoadingService,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
        this.setAndApplyFilters(this.getEmptyFilters());
    }

    /**
     * This method overwrites the currently selected filters in the UI and sets appliedFilters accordingly.
     * It is used for initializing and resetting the filters.
     * It does not trigger a filter change event.
     * @param filters The new filters to apply.
     */
    private setAndApplyFilters(filters: AnalyzeFilters): void {
        this.filters = filters;
        this.appliedFilters = CloneUtils.cloneData(this.filters);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    ngOnInit(): void {
        // init setting of state options
        this.initStateOptions();
        this.subscriptions.add(
            this.translate.onLangChange.subscribe(() => {
                this.initStateOptions();
            }),
        );

        const onInitOrChange = (trans: AngularTranslations, groups: GroupForm[], account: Account): void => {
            this.updateUsersFilter(trans, groups, account.users);

            // Note: activeUsersFilterOptions is used by add-activity modal to display the user select options
            this.activeUsersFilterOptions = SharedFunctions.getUserFilterOptions(account.users);

            this.setLayerOptions(this.layers, this.tiles);
            this.afterInit();
        };

        this.initMgr
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (currentUser) => {
                    this.currentUser = currentUser;
                    this.licenseStatePro = currentUser.isPro();
                    this.userDateFormat = currentUser.getDateFormat();
                    this.firstDayOfWeek = currentUser.getFirstDayOfWeek();
                    this.isAdminOrManager = currentUser.isAdminOrManager();
                },
            })
            .addDependency(this.userService.account$)
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers) => {
                    this.layers = layers;
                },
            })
            .addDependency(this.tileService.tiles$, {
                name: 'tiles$',
                callback: (tiles) => {
                    this.tiles = tiles.filter((t) => !t.archived);
                },
            })
            .addDependency(this.groupService.getAllGroups())
            .addDependency(this.translate.get(['member_option', 'group_option']))
            .onInit(async ([_currentUser, account, _layers, _tiles, groups, trans]) => {
                onInitOrChange(trans as AngularTranslations, groups as GroupForm[], account as Account);
            })
            .onChange(async ([_currentUser, account, _layers, _tiles, groups, trans]) => {
                onInitOrChange(trans as AngularTranslations, groups as GroupForm[], account as Account);
            })
            .initialize();
    }

    private updateUsersFilter(trans: AngularTranslations, groups: GroupForm[], activeUsers: UserForm[]): void {
        const groupItems: SimpleAssignmentOptionItem<TeamFilterOption>[] = groups.map((group: GroupForm) => {
            return {
                label: group.name,
                value: {
                    type: 'groups',
                    id: group.id,
                    name: group.name,
                },
            };
        });

        const groupOptions: SimpleAssignmentOptions<TeamFilterOption> = {
            label: trans['group_option'],
            value: 'groups',
            items: groupItems,
        };

        const userOptions: SimpleAssignmentOptions<TeamFilterOption> = {
            label: trans['member_option'],
            value: 'members',
            items: activeUsers.map((member: UserForm) => ({
                label: `${member.firstName} ${member.lastName}`,
                value: {
                    type: 'members',
                    id: member.id,
                    name: `${member.firstName} ${member.lastName}`,
                },
            })),
        };

        this.teamOptions = [groupOptions, userOptions];

        this.usersFilterOptions = SharedFunctions.getUserFilterOptions(activeUsers);
    }

    afterInit(): void {
        const filtersFromStorage = LocalStorageService.getAnalyzeFilters(this.currentUser!.id);

        if (filtersFromStorage === null) {
            this.setAndApplyFilters(this.getEmptyFilters());
        } else {
            const analyzeFilters = analyzeFiltersType(filtersFromStorage);

            if (analyzeFilters instanceof type.errors) {
                console.warn(
                    'AnalyzeComponent.afterInit() failed to parse filters from LocalStorage, using empty filters:',
                    analyzeFilters.summary,
                );
                this.setAndApplyFilters(this.getEmptyFilters());
            } else {
                this.sanitizeLayerfilters(analyzeFilters);
                this.setAndApplyFilters(analyzeFilters);
            }
        }
        this.appliedFilters = this.filters;

        this.initialized = true;
        this.applyFilters({ storeFilters: false });
    }

    private sanitizeLayerfilters(analyzeFilters: AnalyzeFilters): void {
        analyzeFilters.selectedLayer1Filter = this.getValidLayerFilter(analyzeFilters.selectedLayer1Filter, 1);
        analyzeFilters.selectedLayer2Filter = this.getValidLayerFilter(analyzeFilters.selectedLayer2Filter, 2);
        analyzeFilters.selectedLayer3Filter = this.getValidLayerFilter(analyzeFilters.selectedLayer3Filter, 3);
    }

    private getValidLayerFilter(layerFilter: LayerFilter[], layerIndex: number): LayerFilter[] {
        // this should actually not happen - defensive check:
        if (layerFilter === undefined) {
            return layerFilter;
        }
        if (layerFilter.length === 0) {
            return layerFilter;
        }

        if (this.layers.length < layerIndex) {
            return [];
        }

        const layer = this.layers[layerIndex - 1];

        if (layer.visible === false) {
            return [];
        }

        // remove deleted or archived tiles from filter selection:
        const validLayerFilter = layerFilter.filter((layerFilter) =>
            this.tiles.some((t) => t.id === layerFilter.id && !t.archived),
        );

        // update name - this is actually only needed because of the LayerFilter type containing a name
        // TODO fix type and logic to only work with ids and remove name
        validLayerFilter.forEach((layerFilter) => {
            const tile = this.tiles.find((t) => t.id === layerFilter.id && t.layer === layer.id);
            if (tile) {
                layerFilter.name = tile.name;
            }
        });

        return validLayerFilter;
    }

    private initStateOptions(): void {
        this.translate.get(['nav_summary', 'nav_detailed']).subscribe((translations) => {
            this.stateOptions = [
                {
                    label: translations.nav_summary,
                    value: AnalyzeState.overview,
                },
                {
                    label: translations.nav_detailed,
                    value: AnalyzeState.detail,
                },
            ];

            this.route.params.subscribe((params: Params) => {
                const state = AnalyzeState[params['state'] as keyof typeof AnalyzeState];
                if (state) {
                    this.analyzeState = state;
                    this.applyFilters({ storeFilters: false });
                }
                const breadcrumbKey = params['state'] === 'detail' ? 'nav_detailed' : 'nav_summary';
                this.breadcrumbService.addBreadcrumb(AnalyzeComponent, { breadcrumbKey });
            });
        });
    }

    private applyFilters(opts?: { storeFilters?: boolean; onlyDateRange?: boolean }): void {
        if (opts?.onlyDateRange) {
            this.appliedFilters.dateRange = CloneUtils.cloneData(this.filters.dateRange);
            this.appliedFilters.dateRangeOption = CloneUtils.cloneData(this.filters.dateRangeOption);
        } else {
            this.appliedFilters = CloneUtils.cloneData(this.filters);
        }
        if (opts?.storeFilters ?? true) {
            LocalStorageService.storeAnalyzeFilters(this.currentUser!.id, this.appliedFilters);
        }
        this.execAfterNextRender(() => {
            this.eventsSubject.next(this.appliedFilters);
        });
    }

    uniqueSorted(arr: LayerFilter[]): LayerFilter[] {
        const unique = new Map(arr.map((item: LayerFilter) => [item.id, item])).values();
        return [...unique].sort((a, b) => a.name.localeCompare(b.name));
    }

    // Set the layer options for the filter
    setLayerOptions(_layers: Layer[], _tiles: Tile[]): void {
        this.layers.forEach((layer) => {
            const tilesOfLayer = this.tiles.filter((t) => t.layer === layer.id);
            const temp = tilesOfLayer.map((t) => {
                const layerFilter: LayerFilter = {
                    name: t.name,
                    id: t.id,
                };
                return layerFilter;
            });
            const filterOptions = this.uniqueSorted(temp);

            if (layer.index === 1) {
                this.layer1FilterOptions = filterOptions;
            } else if (layer.index === 2) {
                this.layer2FilterOptions = filterOptions;
            } else if (layer.index === 3) {
                this.layer3FilterOptions = filterOptions;
            }
        });
    }

    private getNextLayerFilterOptions(changedLayerFilter: LayerFilter[], layerIndex: number): LayerFilter[] {
        const slL1Tiles = this.tiles.filter((t) => changedLayerFilter.some((l) => l.id === t.id));
        const tilesDeepCopy = JSON.parse(JSON.stringify(this.tiles));
        // Collect all children from slL1Tiles
        const slL1Children = slL1Tiles.flatMap((tile: Tile) => tile.children ?? []);
        // Update layer2Filter dynamically based on selectedLayer1Filter
        const nextLayerFilterOptions = [
            // Find all children
            ...(slL1Children
                .map((child: number) => tilesDeepCopy.find((t: Tile) => t.id === child && !t.archived)!)
                ?.filter((t: Tile) => t !== undefined) ?? []),
            // Include global tiles from tilesDeepCopy
            ...tilesDeepCopy.filter(
                (t: Tile) => t.layer === this.layers[layerIndex - 1]?.id && !t.archived && t.type === TileType.Global,
            ),
        ];
        // sort alphabetically
        const sorted = this.uniqueSorted(nextLayerFilterOptions);
        // Note: The old implementation returned tile objects here, not the LayerFilter objects.
        // This caused issues with the loading of stored filters. The type is now correctly mapped!
        return sorted.map((layerFilter) => {
            return { name: layerFilter.name, id: layerFilter.id };
        });
    }

    // Updates this.layer2Filter based on selectedLayer1Filter
    onLayer1SlChange(_event: MultiSelectOnChangeEvent<LayerFilter>): void {
        if (this.filters.selectedLayer1Filter.length === 0) {
            this.setLayerOptions(this.layers, this.tiles);
            return;
        }
        this.layer2FilterOptions = this.getNextLayerFilterOptions(this.filters.selectedLayer1Filter, 2);
        const layer2FilterLen = this.filters.selectedLayer2Filter.length;
        if (layer2FilterLen > 0) {
            this.filters.selectedLayer2Filter = this.layer2FilterOptions.filter((o) =>
                this.filters.selectedLayer2Filter.some((l) => l.id === o.id),
            );
            if (this.filters.selectedLayer2Filter.length < layer2FilterLen) {
                this.onLayer2SlChange();
            }
        }
    }

    // updates this.layer3Filter based on selectedLayer2Filter
    onLayer2SlChange(): void {
        if (this.filters.selectedLayer2Filter.length === 0) {
            this.setLayerOptions(this.layers, this.tiles);
            return;
        }
        this.layer3FilterOptions = this.getNextLayerFilterOptions(this.filters.selectedLayer2Filter, 3);
        const layer3FilterLen = this.filters.selectedLayer3Filter.length;
        if (layer3FilterLen > 0) {
            this.filters.selectedLayer3Filter = this.layer3FilterOptions.filter((o) =>
                this.filters.selectedLayer3Filter.some((l) => l.id === o.id),
            );
        }
    }

    showMultiSelect(): boolean {
        const members = this.teamOptions.find((obj) => obj.value === 'members');
        return members ? members?.items.length > 1 && this.layers.length > 0 : false;
    }

    toggleState(value: string): void {
        const state = AnalyzeState[value as keyof typeof AnalyzeState];
        if (state) {
            this.analyzeState = state;
            void this.router.navigateByUrl('/analyze/' + state);
        }
    }

    onDateChanged(event: DateRangeChangedEvent): void {
        if (event.dateRange) {
            this.filters.dateRange = event.dateRange;
            this.filters.dateRangeOption = event.dateRangeOption;
            this.applyFilters({ onlyDateRange: true });
        }
    }

    getLayerPlaceholder(layerIndex: number): string {
        return this.layers.find((layer) => layer.index === layerIndex)?.name ?? '';
    }

    filterButtonClicked(): void {
        this.applyFilters();
        void this.trackingService.pushGtmTag({
            event: 'button-click',
            data: 'apply-filter',
        });
    }

    onFilterChange<K extends keyof AnalyzeFilters>(filterField: K, event: AnalyzeFilters[K]): void {
        // rebuild filter object to change reference -> trigger change detection
        this.filters = { ...this.filters, [filterField]: event };
    }

    private getEmptyFilters(): AnalyzeFilters {
        return {
            dateRange: DateRangeHelper.default(),
            dateRangeOption: 'last30days',
            selectedLayer1Filter: [],
            selectedLayer2Filter: [],
            selectedLayer3Filter: [],
            filterDescription: '',
            isBilled: undefined,
            isNoteAvailable: undefined,
            selectedTeamFilter: null,
        };
    }

    public clearFilter(): void {
        // reset filters except for the date range
        const filters = this.getEmptyFilters();
        filters.dateRange = this.filters.dateRange;
        filters.dateRangeOption = this.filters.dateRangeOption;
        this.setAndApplyFilters(filters);

        LocalStorageService.removeAnalyzeFilters(this.currentUser!.id);

        this.execAfterNextRender(() => {
            this.filterButtonClicked();
        });
    }

    isFilterActive(): boolean {
        return (
            this.filters.selectedLayer1Filter.length > 0 ||
            this.filters.selectedLayer2Filter.length > 0 ||
            this.filters.selectedLayer3Filter.length > 0 ||
            this.filters.filterDescription.length > 0 ||
            this.filters.isBilled != undefined ||
            this.filters.isNoteAvailable != null ||
            this.filters.selectedTeamFilter != null
        );
    }

    onHasDataChanged(hasData: boolean): void {
        this.hasData = hasData;
    }

    private execAfterNextRender(fn: () => void): void {
        // TODO fix hacky timeout workaround
        setTimeout(() => {
            fn();
        }, 10);
    }
}
